/**
 * ApiService - A centralized HTTP client for making API requests.
 * 
 * Uses Axios to handle HTTP requests with a pre-configured instance,
 * including authorization headers, error handling, and response interception.
 * 
 * @module ApiService
 */
import axios from "axios";
import { BASE_URL, token } from "./index";
import { showErrorAlert } from "../components/shared/alerts";

/**
 * Axios instance pre-configured with base settings.
 */
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    //This header instruct the browser on how long the assets should be cached
    "Cache-Control": "max-age=31536000",
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Axios response interceptor for centralized error handling.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      window.location.replace("/forbidden"); // Replace history entry so Back skips the blocked page
    }
    else {//Centralize the display of errors returned from the backend
      const errorMessage =
        error.response?.data?.message ||
        (error.request
          ? "Keine Antwort vom Server erhalten."
          : "Ein unerwarteter Fehler ist aufgetreten.");
      
      // Show the error modal
      showErrorAlert(errorMessage);
    }

    return Promise.reject(error);
  }
);

/**
 * @typedef {Object} ApiService
 * @property {function(string, object=): Promise<any>} get - Sends a GET request.
 * @property {function(string, object=): Promise<any>} post - Sends a POST request.
 * @property {function(string, FormData): Promise<any>} postFormData - Sends a POST request with form data (for file uploads, etc).
 * @property {function(string, object=): Promise<any>} put - Sends a PUT request.
 * @property {function(string, object=): Promise<any>} patch - Sends a PATCH request.
 * @property {function(string): Promise<any>} delete - Sends a DELETE request.
 */

/**
 * An object that exposes methods for HTTP requests.
 */
export const ApiService = {
  /**
  * Sends a GET request.
  * @param {string} url - The endpoint URL.
  * @param {object} [params] - Optional query parameters.
  * @returns {Promise<any>} Response data.
  */
  get: async (url: string, params?: object) => {
    try {
      const response = await axiosInstance.get(url, { ...params });
      return response.data;
    } catch (error) {
      // Re-throw the error to allow the caller to handle it
      throw error;
    }
  },

  /**
   * Sends a POST request.
   * @param {string} url - The endpoint URL.
   * @param {object} [data] - Payload to send in the body.
   * @returns {Promise<any>} Response data.
   */
  post: async (url: string, data?: object) => {
    try {
      const response = await axiosInstance.post(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
  * Sends a POST request with form data (for file uploads).
  * @param {string} url - The endpoint URL.
  * @param {FormData} formData - FormData instance to send.
  * @returns {Promise<any>} Response data.
  */
  postFormData: async (url: string, formData: FormData) => {
    try {
      const response = await axiosInstance.post(url, formData, {
        timeout: 200000,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Sends a PUT request.
   * @param {string} url - The endpoint URL.
   * @param {object} [data] - Payload to send in the body.
   * @returns {Promise<any>} Response data.
   */
  put: async (url: string, data?: object) => {
    try {
      const response = await axiosInstance.put(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
  * Sends a PATCH request.
  * @param {string} url - The endpoint URL.
  * @param {object} [data] - Partial payload to update the resource.
  * @returns {Promise<any>} Response data.
  */
  patch: async (url: string, data?: object) => {
    try {
      const response = await axiosInstance.patch(url, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Sends a DELETE request.
   * @param {string} url - The endpoint URL.
   * @returns {Promise<any>} Response data.
   */
  delete: async (url: string) => {
    try {
      const response = await axiosInstance.delete(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
